import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { PieceResponse } from '@/api/bol';
import {
  formatExternalReferences,
  formatValueWithUnit,
  renderBooleanChip,
} from '@/utils/datagridFormatters';

interface PieceColumnsProps {
  onDelete: (piece: PieceResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createPieceColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: PieceColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'containerType',
    headerName: 'Container Type',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { containerType?: string }) => row.containerType || '-',
  },
  {
    field: 'containerSubtype',
    headerName: 'Container Subtype',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { containerSubtype?: string }) => row.containerSubtype || '-',
  },
  {
    field: 'goodsDescription',
    headerName: 'Goods Description',
    flex: 1.3,
    minWidth: 200,
    valueGetter: (_value: unknown, row: { goodsDescription?: string }) => row.goodsDescription || '-',
  },
  {
    field: 'grossWeight',
    headerName: 'Gross Weight',
    flex: 0.9,
    minWidth: 130,
    valueGetter: (_value: unknown, row: { grossWeight?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.grossWeight?.value, row.grossWeight?.unit),
  },
  {
    field: 'netWeight',
    headerName: 'Net Weight',
    flex: 0.9,
    minWidth: 130,
    valueGetter: (_value: unknown, row: { netWeight?: { value?: number | null; unit?: string | null } }) =>
      formatValueWithUnit(row.netWeight?.value, row.netWeight?.unit),
  },
  {
    field: 'turnable',
    headerName: 'Turnable',
    flex: 0.8,
    minWidth: 110,
    renderCell: (params) => renderBooleanChip(params.row.turnable),
  },
  {
    field: 'stackingMaxNumber',
    headerName: 'Stack Max',
    flex: 0.8,
    minWidth: 110,
    valueGetter: (_value: unknown, row: { stackingMaxNumber?: number }) => row.stackingMaxNumber ?? '-',
  },
  {
    field: 'containedItemsCount',
    headerName: 'Contained Items',
    flex: 0.9,
    minWidth: 130,
    valueGetter: (_value: unknown, row: { containedItems?: unknown[] }) => row.containedItems?.length || 0,
  },
  {
    field: 'containedPiecesCount',
    headerName: 'Contained Pieces',
    flex: 0.9,
    minWidth: 130,
    valueGetter: (_value: unknown, row: { containedPieces?: unknown[] }) => row.containedPieces?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'specialRegulationsCount',
    headerName: 'Special Regulations',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { specialRegulations?: unknown[] }) => row.specialRegulations?.length || 0,
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/pieces/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as PieceResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultPieceColumnVisibilityModel = {
  id: true,
  containerType: true,
  containerSubtype: true,
  goodsDescription: true,
  grossWeight: true,
  netWeight: true,
  turnable: true,
  stackingMaxNumber: false,
  containedItemsCount: false,
  containedPiecesCount: false,
  externalReferences: false,
  specialRegulationsCount: false,
};
